import moment from 'moment';
import {
	getWalkthroughScoreObject,
	nullSafeGet,
	nullSafeGetOrElse,
	nullSafeGetWithZero,
	parseNumber,
} from './DataAccessUtils';
import { isWalthroughTaskAnswered } from './SiteSurveyUtils';

const OVERALL_SCORE_GROUP_TITLE = 'Overall Score';

// Matches the normalization the details page applies in `getSectionKey` (WalkthroughsDetail-
// DetailsPage.tsx): a group title only counts as a section when it has non-whitespace content.
// A stray `' '` from a form that didn't trim, an accidental `'\t'`, or an empty string all
// resolve to the "ungrouped" bucket on the rendered page — so the section rollups here have to
// exclude the same set, or they'll count a section that has no visible header for the user to
// find (the classic "1 section incomplete" tile with nothing on screen to point at).
const isUsableGroupTitle = (groupTitle: any): boolean =>
	typeof groupTitle === 'string' && groupTitle.trim().length > 0;

const SECTION_COMPARISON_BUCKET = {
	ABOVE: 'above',
	EQUAL: 'equal',
	BELOW: 'below',
	IN_PROGRESS: 'inProgress',
	UNSCORED: 'unscored',
};

export const SECTION_COMPARISON_BUCKET_COLORS = {
	[SECTION_COMPARISON_BUCKET.ABOVE]: '#95DE64',
	[SECTION_COMPARISON_BUCKET.EQUAL]: '#D9D9D9',
	[SECTION_COMPARISON_BUCKET.BELOW]: '#FF7875',
	[SECTION_COMPARISON_BUCKET.IN_PROGRESS]: '#FFD666',
	[SECTION_COMPARISON_BUCKET.UNSCORED]: '#BFBFBF',
};

const SECTION_COMPARISON_BUCKET_LABELS = {
	[SECTION_COMPARISON_BUCKET.ABOVE]: '> Last 3 Average',
	[SECTION_COMPARISON_BUCKET.EQUAL]: '= Last 3 Average',
	[SECTION_COMPARISON_BUCKET.BELOW]: '< Last 3 Average',
	[SECTION_COMPARISON_BUCKET.IN_PROGRESS]: 'In Progress',
	[SECTION_COMPARISON_BUCKET.UNSCORED]: 'No Comparison',
};

const SECTION_COMPARISON_BUCKET_ORDER = [
	SECTION_COMPARISON_BUCKET.ABOVE,
	SECTION_COMPARISON_BUCKET.EQUAL,
	SECTION_COMPARISON_BUCKET.BELOW,
	SECTION_COMPARISON_BUCKET.IN_PROGRESS,
	SECTION_COMPARISON_BUCKET.UNSCORED,
];

export const SECTION_SORT_MODE = {
	WALKTHROUGH: 'walkthrough',
	SCORE_DESC: 'scoreDesc',
	SCORE_ASC: 'scoreAsc',
};

export const getSectionSortOptions = (isUnscored: boolean) => {
	const metricLabel = isUnscored ? 'Completion' : 'Score';
	return [
		{ value: SECTION_SORT_MODE.WALKTHROUGH, label: 'Same as Walkthrough' },
		{ value: SECTION_SORT_MODE.SCORE_DESC, label: `${metricLabel} (High to Low)` },
		{ value: SECTION_SORT_MODE.SCORE_ASC, label: `${metricLabel} (Low to High)` },
	];
};

const EQUAL_BAND_PERCENTAGE_POINTS = 0.5;

export interface WalkthroughSectionRow {
	groupTitle: string;
	orderingIndex: number;
	answeredCount: number;
	totalCount: number;
	completionPercentage: number;
	percentageScore: number | null;
	score: number | null;
	maxPossibleScore: number | null;
	averagePercentageScore: number | null;
	averageScore: number | null;
	averageMaxPossibleScore: number | null;
	bucket: string;
}

const getWalkthroughSectionOrder = (walkthrough): string[] => {
	const seen: string[] = [];
	const addSection = (groupTitle) => {
		if (!isUsableGroupTitle(groupTitle)) return;
		if (seen.indexOf(groupTitle) === -1) seen.push(groupTitle);
	};

	const taskAnswers = nullSafeGetOrElse('taskAnswers', walkthrough, []);
	[...taskAnswers]
		.sort(
			(first, second) =>
				nullSafeGetOrElse('orderingId', first, 0) - nullSafeGetOrElse('orderingId', second, 0)
		)
		.forEach((taskAnswer) => addSection(nullSafeGet('groupTitle', taskAnswer)));

	nullSafeGetOrElse('scores', walkthrough, []).forEach((scoreEntry) => {
		const groupTitle = nullSafeGet('groupTitle', scoreEntry);
		if (groupTitle === OVERALL_SCORE_GROUP_TITLE) return;
		addSection(groupTitle);
	});

	return seen;
};

export const getWalkthroughAnsweredSummary = (walkthrough) => {
	const taskAnswers = nullSafeGetOrElse('taskAnswers', walkthrough, []);
	const answeredCount = taskAnswers.filter(isWalthroughTaskAnswered).length;

	const countsByGroup = taskAnswers
		.filter((taskAnswer) => isUsableGroupTitle(nullSafeGet('groupTitle', taskAnswer)))
		.reduce((accumulator, taskAnswer) => {
			const groupTitle = nullSafeGet('groupTitle', taskAnswer);
			const current = accumulator[groupTitle] || { answered: 0, total: 0 };
			accumulator[groupTitle] = {
				answered: current.answered + (isWalthroughTaskAnswered(taskAnswer) ? 1 : 0),
				total: current.total + 1,
			};
			return accumulator;
		}, {});

	const incompleteSectionCount = Object.keys(countsByGroup).filter((groupTitle) => {
		const counts = countsByGroup[groupTitle];
		return counts.answered < counts.total;
	}).length;

	return { answeredCount, totalCount: taskAnswers.length, incompleteSectionCount };
};

export const getWalkthroughDurationMinutes = (walkthrough): number | null => {
	const startTime = nullSafeGet('startTime', walkthrough);
	// `endTime` is only stamped once every question is answered, so fall back to `completedAt`.
	const endTime = nullSafeGet('endTime', walkthrough) || nullSafeGet('completedAt', walkthrough);
	if (!startTime || !endTime) return null;
	const seconds = moment(endTime).diff(moment(startTime), 'seconds');
	const minutes = Math.round(seconds / 60);
	return minutes >= 0 ? minutes : null;
};

const getBucket = (
	answeredCount: number,
	totalCount: number,
	percentageScore: number | null,
	averagePercentageScore: number | null
): string => {
	if (totalCount === 0 || answeredCount < totalCount) return SECTION_COMPARISON_BUCKET.IN_PROGRESS;
	if (percentageScore === null || averagePercentageScore === null) {
		return SECTION_COMPARISON_BUCKET.UNSCORED;
	}
	const difference = percentageScore - averagePercentageScore;
	if (difference > EQUAL_BAND_PERCENTAGE_POINTS) return SECTION_COMPARISON_BUCKET.ABOVE;
	if (difference < -EQUAL_BAND_PERCENTAGE_POINTS) return SECTION_COMPARISON_BUCKET.BELOW;
	return SECTION_COMPARISON_BUCKET.EQUAL;
};

export const getWalkthroughSectionRows = (walkthrough, history): WalkthroughSectionRow[] => {
	const sectionOrder = getWalkthroughSectionOrder(walkthrough);
	const taskAnswers = nullSafeGetOrElse('taskAnswers', walkthrough, []);
	const averageBySection = nullSafeGetOrElse('averages.bySection', history, {});

	return sectionOrder.map((groupTitle, orderingIndex) => {
		const sectionAnswers = taskAnswers.filter(
			(taskAnswer) => nullSafeGet('groupTitle', taskAnswer) === groupTitle
		);
		const scoreObject = getWalkthroughScoreObject(walkthrough, groupTitle);
		const sectionAverage = averageBySection[groupTitle] || {};

		const answeredCount =
			sectionAnswers.length > 0
				? sectionAnswers.filter(isWalthroughTaskAnswered).length
				: nullSafeGetOrElse('questionsAnsweredCount', scoreObject, 0);
		const totalCount =
			sectionAnswers.length > 0
				? sectionAnswers.length
				: nullSafeGetOrElse(
						'questionsCount',
						scoreObject,
						nullSafeGetOrElse('totalQuestionsCount', scoreObject, 0)
				  );
		const percentageScore = parseNumber(nullSafeGetWithZero('percentageScore', scoreObject));
		const averagePercentageScore = parseNumber(
			nullSafeGetWithZero('percentageScore', sectionAverage)
		);

		return {
			groupTitle,
			orderingIndex,
			answeredCount,
			totalCount,
			completionPercentage: totalCount > 0 ? (answeredCount * 100) / totalCount : 0,
			percentageScore,
			score: parseNumber(nullSafeGetWithZero('score', scoreObject)),
			maxPossibleScore: parseNumber(nullSafeGetWithZero('maxPossibleScore', scoreObject)),
			averagePercentageScore,
			averageScore: parseNumber(nullSafeGetWithZero('score', sectionAverage)),
			averageMaxPossibleScore: parseNumber(nullSafeGetWithZero('maxPossibleScore', sectionAverage)),
			bucket: getBucket(answeredCount, totalCount, percentageScore, averagePercentageScore),
		};
	});
};

export const sortWalkthroughSectionRows = (
	rows: WalkthroughSectionRow[],
	sortMode: string,
	metric: 'percentageScore' | 'completionPercentage' = 'percentageScore'
): WalkthroughSectionRow[] => {
	if (sortMode === SECTION_SORT_MODE.WALKTHROUGH) {
		return [...rows].sort((first, second) => first.orderingIndex - second.orderingIndex);
	}
	const direction = sortMode === SECTION_SORT_MODE.SCORE_ASC ? 1 : -1;
	return [...rows].sort((first, second) => {
		const firstValue = first[metric];
		const secondValue = second[metric];
		if (firstValue === null && secondValue === null) {
			return first.orderingIndex - second.orderingIndex;
		}
		if (firstValue === null) return 1;
		if (secondValue === null) return -1;
		if (firstValue === secondValue) {
			return first.orderingIndex - second.orderingIndex;
		}
		return (firstValue - secondValue) * direction;
	});
};

export const getSectionBucketCounts = (rows: WalkthroughSectionRow[]) => {
	const total = rows.length;
	return SECTION_COMPARISON_BUCKET_ORDER.map((bucket) => {
		const count = rows.filter((row) => row.bucket === bucket).length;
		return {
			bucket,
			count,
			total,
			label: SECTION_COMPARISON_BUCKET_LABELS[bucket],
			color: SECTION_COMPARISON_BUCKET_COLORS[bucket],
			percentage: total > 0 ? Math.round((count * 100) / total) : 0,
		};
	});
};
